import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Typography,
} from "@mui/joy";
import {
    Domain,
    EmojiNature,
    Folder,
    People,
    Settings,
    SportsVolleyball,
} from "@mui/icons-material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { PageStandard } from "../utils/PageStandard";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardStats, getDashboardStats } from "../utils/ViewModel";

const QUICK_LINKS = [
    { label: "Esperienze di gioco", icon: <SportsVolleyball />, path: "/game", color: "#1976d2" },
    { label: "Utenti", icon: <People />, path: "/users", color: "#388e3c" },
    { label: "Organizzazioni", icon: <Domain />, path: "/organizzazioni", color: "#7b1fa2" },
    { label: "Awards", icon: <EmojiEventsIcon />, path: "/awards", color: "#f57c00" },
    { label: "Stickers", icon: <EmojiNature />, path: "/stickers", color: "#0288d1" },
    { label: "Storage", icon: <Folder />, path: "/storage", color: "#455a64" },
    { label: "Settings", icon: <Settings />, path: "/settings", color: "#616161" },
];

export function Dashboard() {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getDashboardStats().then((s) => {
            setStats(s);
            setLoading(false);
        });
    }, []);

    return (
        <PageStandard title="Dashboard" sidebarVisible={true}>
            <Box sx={{ maxWidth: 900 }}>

                {/* Stats */}
                <Typography level="title-md" sx={{ mb: 1.5 }}>
                    Statistiche
                </Typography>
                {loading ? (
                    <CircularProgress size="sm" sx={{ mb: 3 }} />
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                            gap: 2,
                            mb: 4,
                        }}
                    >
                        <StatCard label="Utenti registrati" value={stats?.total_users} color="primary" />
                        <StatCard label="Esperienze pubbliche" value={stats?.public_experiences} color="success" />
                        <StatCard label="Bozze" value={stats?.draft_experiences} color="warning" />
                        <StatCard label="Organizzazioni" value={stats?.total_organizations} color="neutral" />
                        <StatCard label="Stickers" value={stats?.total_stickers} color="neutral" />
                    </Box>
                )}

                {/* Quick links */}
                <Typography level="title-md" sx={{ mb: 1.5 }}>
                    Accesso rapido
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                        gap: 2,
                    }}
                >
                    {QUICK_LINKS.map((item) => (
                        <Link key={item.path} to={item.path} style={{ textDecoration: "none" }}>
                            <Card
                                variant="outlined"
                                sx={{
                                    cursor: "pointer",
                                    transition: "box-shadow 0.15s, transform 0.15s",
                                    "&:hover": {
                                        boxShadow: "md",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <CardContent sx={{ alignItems: "center", textAlign: "center", gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: item.color + "20",
                                            color: item.color,
                                            fontSize: 24,
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                    <Typography level="title-sm" sx={{ mt: 0.5 }}>
                                        {item.label}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    ))}
                </Box>
            </Box>
        </PageStandard>
    );
}

function StatCard({
    label,
    value,
    color,
}: {
    label: string;
    value: number | undefined;
    color: "primary" | "success" | "warning" | "neutral" | "danger";
}) {
    return (
        <Card variant="soft" color={color}>
            <CardContent>
                <Typography level="h2" color={color}>
                    {value ?? "—"}
                </Typography>
                <Typography level="body-sm" color={color}>
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );
}
